import { EventEmitter } from 'events';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { App } from '@capacitor/app';
import Nprogress from 'nprogress';
import { ToastContainer } from 'react-toastify';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { wifi } from 'capacitor-jd-wifi';
import lookie from 'lookie';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import { throttle } from 'lodash-es';
import { TrackerCustomEvent } from 'common-package';
import { useLatest } from 'ahooks';
import { Dialog } from 'antd-mobile';
import { isNewSoftwareVersion } from '@/utils';
import {
  selectedApkUpdateStatus,
  setDevicePlatform,
  setDeviceUuid,
  setShouldAlertUpdateH5AfterAIConsultation,
} from '@/store/device';
import service from '@/service';
import { TabBar, ApkUpdater } from '@/components';
import useGetLocation from '@/hooks/useGetLocation';
import { TOAST_CONTAINER_ID, TAB_BAR_HIDE_ROUTE, IGNORE_UPDATE_PAD_IDS, CURRENT_ANDROID_ID } from '@/constant';
import { useAppCrash, useClearWebviewCache, useTracker, useUpdateProgram } from '@/hooks';
import cx from './index.module.scss';

export const eventBus = new EventEmitter();

const BasicLayout: React.FC = () => {
  const apkUpdateStatusRef = useLatest(useSelector(selectedApkUpdateStatus));
  const location = useLocation();
  const dispatch = useDispatch();
  const { sendEvent } = useTracker();
  const lastNetworkInfo = useRef('');
  const [windowHeight, setWindowHeight] = useState(window?.innerHeight);

  const isShowTabBar = useMemo(() => !TAB_BAR_HIDE_ROUTE.includes(location.pathname), [location?.pathname]);

  /** 当前是否在AI问询流程 */
  const currentIsInAIConsultationProcess = useLatest(
    useMemo(() => location?.pathname?.includes('ai-consultation'), [location?.pathname])
  );

  const onUpdate = useCallback(() => {
    // 开发模式忽略更新
    if (import.meta.env?.DEV) return;

    Device.getId()
      .then(info => {
        /** 判断当前pad id是否在忽略更新列表 */
        if (!IGNORE_UPDATE_PAD_IDS.includes(info.uuid)) {
          if (currentIsInAIConsultationProcess.current) {
            dispatch(setShouldAlertUpdateH5AfterAIConsultation(true));
            console.log('H5更新flag设置完成');
            return;
          }

          Dialog.alert({
            title: '检测到更新',
            content: (
              <div>
                <span>我们对产品进行了修复和优化，</span>
                <br />
                <span>请尽快完成更新</span>
              </div>
            ),
            confirmText: '我知道了',
            onConfirm() {
              App.exitApp().catch(err => {
                if (err.message === 'Not implemented on web.') {
                  window?.location?.reload();
                }
              });
            },
          });
        }
      })
      .catch(error => {
        Sentry.captureException({
          message: '获取设备id失败',
          errInfo: error,
        });
        sendEvent?.(TrackerCustomEvent.GetAndroidIdError, {
          et: 'monitor',
          c1: error,
        });
        console.error('获取设备id失败', error);
      });
  }, [sendEvent]);

  useGetLocation();
  useUpdateProgram({
    loopInterval: 30 * 60 * 1000,
    shouldAlertUpdateMetaName: 'app-should-alert-update',
    updateLogMetaName: 'app-update-log',
    onUpdate,
  });
  useAppCrash();
  useClearWebviewCache();

  const onWindowResizeChange = useMemo(() => throttle(e => setWindowHeight(e?.target?.innerHeight), 300), []);

  /** 获取设备信息 */
  const getDeviceId = useCallback(() => {
    Device.getId()
      .then(info => {
        dispatch(setDeviceUuid(info.uuid));
      })
      .catch(error => {
        Sentry.captureException({
          message: '获取设备id失败',
          errInfo: error,
        });
        sendEvent(TrackerCustomEvent.GetAndroidIdError, {
          et: 'monitor',
          c1: error,
        });
        console.error('获取设备id失败', error);
      });
  }, [dispatch, sendEvent]);

  const initVConsole = useCallback(async () => {
    // 只在非prod环境加载vconsole资源脚本
    if (!['prod'].includes(__BUILD_ENV__)) {
      Device.getInfo()
        .then(async result => {
          if (result.platform !== 'web') {
            try {
              const Vconsole = (await import('vconsole')).default;
              new Vconsole({
                defaultPlugins: ['system'],
                log: {
                  maxLogNumber: 50,
                },
              });
              // window.eruda.init({
              //   useShadowDom: true,
              //   // ['console', 'elements', 'network', 'resources', 'sources', 'info', 'snippets']
              //   tool: ['console', 'info'],
              // });
              // const erudaConsole = window.eruda.get('console');
              // erudaConsole.config.set('maxLogNum', '50');
              // erudaConsole.log('已设置最大log数量：50');
            } catch (e) {
              console.error('VConsole error:', e);
            }
          }
        })
        .catch(e => {
          console.log('获取设备信息失败', e);
        });
    }
  }, []);

  const initNetworkListener = useCallback(async () => {
    const deviceInfo = await Device.getInfo();
    dispatch(setDevicePlatform(deviceInfo?.platform));
    if (deviceInfo?.platform !== 'android') {
      return;
    }
    const appInfo = await App.getInfo();
    if (isNewSoftwareVersion(appInfo?.version, '1.12.0')) {
      Network.addListener('networkStatusChange', async status => {
        // 当前应用有更新，忽略网络变化上传
        if (apkUpdateStatusRef.current === 'should-update') {
          return;
        }

        if (lastNetworkInfo.current !== JSON.stringify(status)) {
          lastNetworkInfo.current = JSON.stringify(status);
          try {
            const ipInfo = await wifi.getIP();
            const ssidInfo = await wifi.getSSID();

            const params = {
              androidId: lookie.get(CURRENT_ANDROID_ID),
              ip: ipInfo.ip,
              netmask: ipInfo?.netmask,
              ssid: ssidInfo.ssid,
              errorMessage: null,
              ...status,
            };

            service.system.logNetwork(params);
          } catch (e) {
            const errParams = {
              androidId: lookie.get(CURRENT_ANDROID_ID),
              errorMessage: e?.toString(),
            };

            service.system.logNetwork(errParams);
          }
        }
      });
    }
  }, []);

  const initBackButtonListener = useCallback(() => {
    // 覆盖系统默认的返回策略
    App.addListener('backButton', () => {});
  }, []);

  const styleObj = useMemo(
    () =>
      ({
        '--window-height': windowHeight + 'px',
      } as React.CSSProperties),
    [windowHeight]
  );

  useEffect(() => {
    if (sendEvent) {
      getDeviceId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendEvent]);

  useEffect(() => {
    document.addEventListener('contextmenu', e => {
      e?.preventDefault();
    });

    initVConsole();
    initNetworkListener();
    initBackButtonListener();

    window?.addEventListener('resize', onWindowResizeChange);

    Nprogress.configure({
      minimum: 0.01,
      speed: 600,
      showSpinner: false,
      positionUsing: 'translate',
      parent: '#nprogress-wrapper',
    });

    return () => {
      App.removeAllListeners();
      window?.removeEventListener('resize', onWindowResizeChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cx('basic-layout')} style={styleObj}>
      <div id="nprogress-wrapper" />
      <div className={cx('tab-bar-wrapper', { hide: !isShowTabBar })}>
        <TabBar />
      </div>
      <div className={cx('main-content', { 'full-height': !isShowTabBar })}>
        <Outlet />
      </div>
      <ToastContainer
        enableMultiContainer={true}
        containerId={TOAST_CONTAINER_ID.BasicLayout}
        closeButton={false}
        hideProgressBar={true}
        draggable={false}
        position="bottom-left"
        closeOnClick={false}
        pauseOnFocusLoss={false}
        pauseOnHover={false}
      />
      <ApkUpdater />
    </div>
  );
};

export default BasicLayout;
